import { apiBase } from "@/lib/api-base";

// Lightweight shielded pool reads. These are plain server fetches with no crypto
// dependencies (no ethers, circomlibjs, or merkle tree), kept separate from
// pool.ts so callers that only need pool metadata, the Info tab's counts and the
// relayer-availability poll, never pull in the heavy proving client.

// Which shielded pool to read or relay for. The server defaults to "01" when the
// query param is omitted, so existing light callers keep working unchanged.
export type ShieldedPoolId = "01" | "usd0";

export interface PoolState {
  notes: { index: number; commitment: string; encryptedOutput: string }[];
  nullifiers: string[];
  root: string;
  nextIndex: number;
}

export interface RelayerInfo {
  configured: boolean;
  funded: boolean;
  relayerAddress: string | null;
  fee: string;
  feeTokenDecimals: number;
  // The symbol the relayer fee is charged in (the selected pool's token).
  feeTokenSymbol: string;
  ethBalance: string;
}

export async function fetchPoolState(pool: ShieldedPoolId = "01"): Promise<PoolState> {
  const res = await fetch(`${apiBase()}/shielded/state?pool=${pool}`);
  if (!res.ok) throw new Error("could not read shielded pool state");
  return (await res.json()) as PoolState;
}

export async function fetchRelayerInfo(pool: ShieldedPoolId = "01"): Promise<RelayerInfo> {
  const res = await fetch(`${apiBase()}/relayer/info?pool=${pool}`);
  if (!res.ok) throw new Error("could not read relayer info");
  return (await res.json()) as RelayerInfo;
}

// Ask the server for the ids of loans whose borrowed USDS went to `address`,
// so the borrower can find and repay their own loans. Loans are discovered by
// their public USDS recipient, so this only covers that public side. It is a
// plain fetch, so listing a wallet's loans never pulls the heavy proving client.
export async function fetchLoansFor(address: string): Promise<bigint[]> {
  const res = await fetch(
    `${apiBase()}/lending/loans?address=${encodeURIComponent(address)}`,
  );
  if (!res.ok) throw new Error("could not read your loans");
  const body = (await res.json()) as { loanIds: string[] };
  return body.loanIds.map((id) => BigInt(id));
}

// Ask the server for every loan id ever opened, so a liquidator can read each
// loan's live health on chain and find the unhealthy ones. A plain fetch, so
// listing loans never pulls the heavy proving client.
export async function fetchAllLoans(): Promise<bigint[]> {
  const res = await fetch(`${apiBase()}/lending/all-loans`);
  if (!res.ok) throw new Error("could not read loans");
  const body = (await res.json()) as { loanIds: string[] };
  return body.loanIds.map((id) => BigInt(id));
}

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

// A zeroed Proof + ExtData pair for a partial repay, where the contract does
// not call the pool (the re-shield only runs when a loan fully closes), so
// these values are never verified. Kept here, free of the proving client, so a
// partial repay never downloads the heavy shielded module.
export function emptyTransactArgs() {
  return [
    {
      proof: "0x" as `0x${string}`,
      root: 0n,
      inputNullifiers: [0n, 0n] as const,
      outputCommitments: [0n, 0n] as const,
      publicAmount: 0n,
      extDataHash: ("0x" + "00".repeat(32)) as `0x${string}`,
    },
    {
      recipient: ZERO_ADDRESS as `0x${string}`,
      extAmount: 0n,
      relayer: ZERO_ADDRESS as `0x${string}`,
      fee: 0n,
      encryptedOutput1: "0x" as `0x${string}`,
      encryptedOutput2: "0x" as `0x${string}`,
    },
  ] as const;
}
